import { useReducer, useEffect } from 'react'
import api from '@api'
import {
  Order,
  IPageInfo,
  IBooking,
  IHRLocation,
  IDepartment,
  IWorkplaceGroup,
  ISpace,
  IEmployee,
  IUsageType,
} from '@api/types'
import { uniqueId } from 'lodash'
import { useSelection } from '@hooks'
import { useShouldReload } from '@hooks/reloadContext'
import { IBookingVariables } from '@api/booking/list'
import { IGridTableCell, GridTableStatusEnum } from '@ui'
import moment from 'moment-timezone'
import {
  BaseTableFilters,
  Sort,
  GetVariablesFunc,
  UseBookingsTableHook,
  Actions,
  ActionBase,
} from '../../CurrentBookingsTable/types'

const defaultStart = moment().toDate()
const defaultEnd = moment().add(1, 'month').toDate()
const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

export interface Filters extends BaseTableFilters {
  locations: any[]
  HRLocations: IHRLocation[]
  departments: IDepartment[]
  WPGs: IWorkplaceGroup[]
  spaces: ISpace[]
  employees: IEmployee[]
  usageTypes: IUsageType[]
}

interface State {
  reqId: string
  status: GridTableStatusEnum
  count: number
  items: IGridTableCell<IBooking>[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  first: number
  filters: Filters
}

export interface UseBookingsHook extends UseBookingsTableHook<Filters> {
  nextPage: () => void
  optimisticDelete: (ids: string[]) => void
  fetchExportLink: () => Promise<string>
}

type Action =
  | ActionBase<Filters>
  | {
      type: Actions.LOAD
      reqId: string
      items: IGridTableCell<IBooking>[]
      count: number
      pageInfo: IPageInfo
    }

let persistedState: State = {
  reqId: uniqueId('request'),
  status: GridTableStatusEnum.LOADING,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'startDate', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  filters: {
    query: '',
    fromDate: defaultStart,
    toDate: defaultEnd,
    locations: [],
    HRLocations: [],
    departments: [],
    WPGs: [],
    spaces: [],
    employees: [],
    usageTypes: [],
  },
}

export function getVariables({
  sort,
  after,
  first,
  filters,
}: GetVariablesFunc<Filters>): IBookingVariables {
  const filterList = []

  if (filters.HRLocations.length) {
    filterList.push({
      geoLocationId_in: filters.HRLocations.map((hrLocation) => hrLocation.id),
    })
  }

  if (filters.locations.length) {
    filterList.push({ floorId_in: filters.locations.map((floor) => floor.id) })
  }

  if (filters.departments.length) {
    filterList.push({ departmentId_in: filters.departments.map((d) => d.id) })
  }

  if (filters.spaces.length) {
    filterList.push({ spaceId_in: filters.spaces.map((s) => s.id) })
  }

  if (filters.WPGs.length) {
    filterList.push({ workplaceGroupId_in: filters.WPGs.map((wpg) => wpg.id) })
  }

  if (filters.employees.length) {
    filterList.push({ employeeId_in: filters.employees.map((e) => e.id) })
  }

  if (filters.usageTypes.length) {
    filterList.push({ usageTypeId_in: filters.usageTypes.map((ut) => ut.id) })
  }

  if (filters.fromDate && filters.toDate) {
    const from = `${moment(filters.fromDate).format('YYYY-MM-DD')}T00:00:00.000Z`
    const to = `${moment(filters.toDate).format('YYYY-MM-DD')}T23:59:59.999Z`
    const times = {
      OR: [
        { startDate_between: [from, to] },
        { endDate_between: [from, to] },
        { startDateEndDate_overlaps: [from, to] },
      ],
    }

    // We are getting any bookings that touch the selected dates in the filters
    filterList.push(times)
  }

  const where = {
    AND: filterList,
  }

  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    ...(where.AND.length
      ? {
          where,
        }
      : {}),
  }
}

export default function useBookings(): UseBookingsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()

  const fetchBookings =
    ({
      showSkeleton,
      loadAll,
      usePaging,
      shouldClearSelection = true,
    }: {
      showSkeleton: boolean
      loadAll: boolean
      usePaging: boolean
      shouldClearSelection?: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: usePaging ? state.after : null,
        sort: state.sort,
        first: loadAll ? state.first : PAGE_SIZE,
        filters: state.filters,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.booking.list(variables).then(({ edges, counters, pageInfo }) => {
        const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
        const itemIds: string[] = []

        const items = edges.map(({ node }) => {
          if (isAllItemsSelected) {
            itemIds.push(node.id)
          }

          return { id: node.id, data: node }
        })

        //clear selection outside filtered range
        if (shouldClearSelection) {
          selection.removeSelectionsByItems(items)
        }

        // select all items if the select all is checked
        if (isAllItemsSelected && items.length) {
          selection.addAll(itemIds)
        }

        dispatch({
          type: Actions.LOAD,
          count: counters.total,
          items: items as IGridTableCell<IBooking>[],
          pageInfo,
          reqId,
        })
      })
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(
    fetchBookings({
      showSkeleton: false,
      loadAll: false,
      usePaging: true,
      shouldClearSelection: false,
    }),
    [state.after]
  )

  //api request without skeleton and with load all items in state.first
  useEffect(fetchBookings({ showSkeleton: false, loadAll: true, usePaging: false }), [
    state.sort,
    shouldReload.bookings,
  ])

  //api request with skeleton and with load only 25 items after cursor
  useEffect(fetchBookings({ showSkeleton: true, loadAll: false, usePaging: false }), [
    state.filters,
  ])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  const fetchExportLink = () => {
    const { orderBy, where } = getVariables({
      after: null,
      sort: state.sort,
      first: state.first,
      filters: state.filters,
    })
    return api.booking.csv({ orderBy, where })
  }

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    optimisticDelete: (ids: string[]) => {
      dispatch({ type: Actions.OPTIMISTIC_DELETE, ids })
    },
    selection,
    fetchExportLink,
    resetFilters: () => {
      dispatch({
        type: Actions.RESET_FILTERS,
      })
    },
    applyFilters: (filters: Filters) => {
      dispatch({
        type: Actions.FILTER,
        filters,
      })
    },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId ? { ...state, status: GridTableStatusEnum.IDLE } : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            count: action.count,
            items:
              state.status === GridTableStatusEnum.PAGING
                ? [...state.items, ...action.items]
                : action.items,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        status: GridTableStatusEnum.SORTING,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        status: GridTableStatusEnum.PAGING,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.OPTIMISTIC_DELETE:
      return {
        ...state,
        items: state.items.filter((booking) => !action.ids.includes(booking.data.id)),
      }

    case Actions.RESET_FILTERS:
      return {
        ...state,
        filters: {
          query: '',
          fromDate: defaultStart,
          toDate: defaultEnd,
          locations: [],
          HRLocations: [],
          departments: [],
          WPGs: [],
          spaces: [],
          employees: [],
          usageTypes: [],
        },
      }

    case Actions.FILTER:
      return {
        ...state,
        filters: action.filters,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }

    default:
      return state
  }
}
